import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Landmark, Check, X, FileText, Save, Loader2 } from "lucide-react";
import axios from "axios";
import { api } from "../lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "./ui/dialog";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "./ui/tabs";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Textarea } from "./ui/textarea";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const openReceipt = async (id) => {
  const w = window.open("", "_blank");
  try { const r = await axios.get(`${API}/admin/payment-requests/${id}/receipt`, { responseType: "blob" }); w.location.href = URL.createObjectURL(r.data); }
  catch { w?.close(); toast.error("تعذّر فتح الإيصال"); }
};

const Requests = ({ items, reload }) => {
  const [busy, setBusy] = useState("");
  const decide = async (r, action) => {
    const body = action === "reject" ? { reason: window.prompt("سبب الرفض", "") ?? null } : {};
    if (action === "reject" && body.reason === null) return;
    if (action === "approve" && !window.confirm(`تفعيل ${r.plan_name} لمدة ${r.months} شهر لـ ${r.org_name}؟`)) return;
    setBusy(r.id);
    try { await api.adminPaymentDecide(r.id, action, body); toast.success(action === "approve" ? "تم تفعيل الاشتراك" : "تم الرفض"); reload(); }
    catch (e) { toast.error(e?.response?.data?.detail || "تعذّر التنفيذ"); } finally { setBusy(""); }
  };
  if (!items.length) return <p className="py-10 text-center text-sm text-slate-400">لا توجد طلبات</p>;
  return (
    <div className="space-y-2">
      {items.map((r) => (
        <div key={r.id} className={`flex flex-wrap items-center gap-2 rounded-xl border px-3 py-2 text-sm ${r.status === "pending" ? "border-amber-300 bg-amber-50/50" : "border-slate-200"}`} data-testid={`pay-req-${r.id}`}>
          <div className="min-w-0 flex-1"><p className="font-bold">{r.org_name || r.org_id} — {r.plan_name} · {r.months} شهر</p>
            <p className="text-xs text-slate-500">{r.amount} ر.س{r.discount_pct ? ` (خصم ${r.discount_pct}%)` : ""} · {r.created_by} · {r.created_at?.slice(0, 10)}{r.note ? ` · ${r.note}` : ""}</p></div>
          <Button size="sm" variant="ghost" onClick={() => openReceipt(r.id)} className="h-8 gap-1" data-testid={`pay-req-receipt-${r.id}`}><FileText className="h-3.5 w-3.5" /> الإيصال</Button>
          {r.status === "pending" ? <>
            <Button size="sm" disabled={busy === r.id} onClick={() => decide(r, "approve")} className="h-8 gap-1 bg-emerald-600" data-testid={`pay-req-approve-${r.id}`}><Check className="h-3.5 w-3.5" /> تفعيل</Button>
            <Button size="sm" variant="outline" disabled={busy === r.id} onClick={() => decide(r, "reject")} className="h-8 gap-1 text-red-600" data-testid={`pay-req-reject-${r.id}`}><X className="h-3.5 w-3.5" /> رفض</Button>
          </> : <span className={`text-xs font-bold ${r.status === "approved" ? "text-emerald-600" : "text-red-600"}`}>{r.status === "approved" ? `مُفعّل حتى ${r.subscribed_until?.slice(0, 10)}` : `مرفوض${r.reject_reason ? `: ${r.reject_reason}` : ""}`}</span>}
        </div>
      ))}
    </div>
  );
};

const Settings = () => {
  const [f, setF] = useState(null);
  useEffect(() => { api.adminPlatformSettings().then((s) => setF({ bank_name: "", iban: "", beneficiary: "", transfer_note: "", ...s, trusted: (s.trusted_names || []).join("\n") })).catch(() => {}); }, []);
  if (!f) return <Loader2 className="mx-auto h-5 w-5 animate-spin" />;
  const save = async () => {
    try { await api.adminSavePlatformSettings({ bank_name: f.bank_name, iban: f.iban, beneficiary: f.beneficiary, transfer_note: f.transfer_note, trusted_names: f.trusted.split("\n") }); toast.success("تم الحفظ"); }
    catch (e) { toast.error(e?.response?.data?.detail || "تعذّر الحفظ"); }
  };
  const inp = (k, l) => <label className="block text-xs text-slate-500">{l}<Input value={f[k]} onChange={(e) => setF({ ...f, [k]: e.target.value })} data-testid={`ps-${k}`} /></label>;
  return (
    <div className="space-y-2">
      {inp("bank_name", "اسم البنك")}{inp("iban", "الآيبان")}{inp("beneficiary", "اسم المستفيد")}{inp("transfer_note", "ملاحظة تظهر للعميل")}
      <label className="block text-xs text-slate-500">«موثوق به من قِبل» — اسم عميل في كل سطر (يظهر في الصفحة الرئيسية)
        <Textarea rows={4} value={f.trusted} onChange={(e) => setF({ ...f, trusted: e.target.value })} data-testid="ps-trusted" /></label>
      <Button onClick={save} className="gap-1.5" data-testid="ps-save"><Save className="h-4 w-4" /> حفظ</Button>
    </div>
  );
};

export const PaymentRequestsButton = () => {
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState([]);
  const load = () => api.adminPaymentRequests().then((r) => setItems(r.items || [])).catch(() => {});
  useEffect(() => { load(); }, []);
  const pending = items.filter((r) => r.status === "pending").length;
  return (
    <>
      <Button variant="outline" size="sm" onClick={() => { load(); setOpen(true); }} className="relative gap-1.5 border-amber-200 text-amber-700 hover:bg-amber-50" data-testid="open-pay-requests">
        <Landmark className="h-4 w-4" /> <span className="hidden sm:inline">التحويلات البنكية</span>
        {pending > 0 && <span className="absolute -left-1.5 -top-1.5 grid h-5 min-w-5 place-items-center rounded-full bg-red-600 px-1 text-[10px] font-bold text-white" data-testid="pay-requests-badge">{pending}</span>}
      </Button>
      {open && <Dialog open onOpenChange={setOpen}>
        <DialogContent dir="rtl" className="max-h-[90vh] max-w-2xl overflow-y-auto" data-testid="pay-requests-dialog">
          <DialogHeader className="text-right"><DialogTitle className="font-heading">طلبات الاشتراك بالتحويل البنكي</DialogTitle><DialogDescription>راجع الإيصال ثم فعّل الاشتراك بضغطة</DialogDescription></DialogHeader>
          <Tabs defaultValue="req"><TabsList><TabsTrigger value="req" data-testid="pay-tab-req">الطلبات ({pending})</TabsTrigger><TabsTrigger value="set" data-testid="pay-tab-settings">بيانات الحساب البنكي</TabsTrigger></TabsList>
            <TabsContent value="req" className="mt-3"><Requests items={items} reload={load} /></TabsContent>
            <TabsContent value="set" className="mt-3"><Settings /></TabsContent></Tabs>
        </DialogContent>
      </Dialog>}
    </>
  );
};
